// START IMPORTS
import { publish } from '@ltvco/refresh-lib';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useSession } from '@ltvco/refresh-lib/v1';
import { Dispatch, SetStateAction, useContext } from 'react';
import { NavigateFunction } from 'react-router-dom';

import { constants } from 'appConstants';
// END IMPORTS

// START CONSTANTS
export const AccountInformationOptions = [
  {
    name: 'Edit Name',
    display: 'Edit Name',
    value: 'Edit Name',
    constantValue: 'EDIT_NAME',
  },
  {
    name: 'Edit Email',
    display: 'Edit Email',
    value: 'Edit Email',
    constantValue: 'EDIT_EMAIL',
  },
  {
    name: 'Add Phone Number',
    display: 'Edit Phone Number',
    value: 'Add Phone Number',
    constantValue: 'ADD_PHONE_NUMBER',
  },
  {
    name: 'Change Password',
    display: 'Change Password',
    value: 'Change Password',
    constantValue: 'CHANGE_PASSWORD',
  },
  {
    name: 'Edit Payment Info',
    display: 'Edit Payment Info',
    value: 'Edit Payment Info',
    constantValue: 'EDIT_PAYMENT_INFO',
  },
  {
    name: 'Cancel My Account',
    display: 'Cancel My Account',
    value: 'Cancel My Account',
    constantValue: 'CANCEL_MY_ACCOUNT',
  },
];
// END CONSTANTS

// START HELPER_FUNCTIONS
export const editOptionClicked = (
  editing: string | null,
  // eslint-disable-next-line no-unused-vars
  openModal: (open: boolean) => void,
  setEditing: Dispatch<SetStateAction<string | null>>,
  customOptions?: {
    name: string;
    display: string;
    value: string;
    constantValue: string;
  }[]
) => {
  const options = customOptions || AccountInformationOptions;
  if (editing === 'Edit Payment Info') {
    openModal(true);
  } else {
    setEditing(
      options.find((option) => {
        return option.name === editing;
      })?.constantValue || editing
    );
  }
};

export const GenericCtaCardProps = (
  navigate: NavigateFunction,
  // eslint-disable-next-line no-unused-vars
  onVariationCtaClick: (text: string) => void,
  // eslint-disable-next-line no-unused-vars
  checkIfFeatureIsBlocked: (searchType: string) => boolean,
  isHidden: (searchType: string) => boolean,
  isInUpdatePaymentMethodVariation: boolean,
  openModal: () => void
) => {
  // has_sso_connections
  const {
    session: { account },
  } = useSession();
  const { trackEventGA4 } = useContext(AppConfig);

  const has_sso_connections = account?.account?.user_info?.has_sso_connections;

  const handleCtaClick = (
    ctaLabel: string,
    actionParam: string,
    eventSite: string,
    isPublish: boolean
  ) => {
    trackEventGA4({
      event_name: 'navigation_click',
      type: eventSite,
      interacted_from: 'my_account',
    });

    checkIfFeatureIsBlocked(eventSite)
      ? onVariationCtaClick(ctaLabel)
      : isPublish
      ? publish(actionParam)
      : navigate(actionParam);
  };

  // Google-enabled brands: BeenVerified, Bumper, MoneyBot5000, NeighborWho, Number Guru, Ownerly, PeopleLooker, PeopleSmart, ReversePhone, Wingmate LC 04/07/25
  // Apple-enabled brands: BeenVerified, Bumper, NeighborWho, Number Guru, PeopleLooker, Wingmate LC 04/07/25
  const sso_enabled = {
    google: true,
    apple: true,
  };

  const props = [
    {
      headerText: `${
        has_sso_connections ? '' : 'Connect or '
      } Edit Login Options`,
      bodyText: `Add ${has_sso_connections ? 'or remove' : ''} your ${
        sso_enabled.google && sso_enabled.apple
          ? 'Google or Apple'
          : sso_enabled.google
          ? 'Google'
          : sso_enabled.apple
          ? 'Apple'
          : ''
      } Login ${
        has_sso_connections ? '' : 'on top of your account password & email'
      }`,
      buttonText: 'Edit Login Options',
      imgUrl: constants.images.imgSingleSignOnCta,
      onCtaClick: () => window.open(constants.links.setupSsoLink, '_blank'),
      dataCy: 'login_options_cta',
    },
    ...(isInUpdatePaymentMethodVariation
      ? [
          {
            headerText: 'Update Payment Details',
            bodyText: 'Add or modify your payment methods',
            buttonText: 'Update now',
            imgUrl: constants.images.imgUpdatePaymentDetails,
            onCtaClick: openModal,
          },
        ]
      : []),
    {
      headerText: 'Search property reports',
      bodyText: 'Search an address to find out home value, ownership and more',
      buttonText: 'Search an address now',
      imgUrl: constants.images.imgAddressGeneric,
      onCtaClick: () =>
        handleCtaClick(
          'property report',
          'openPropertySearch',
          'property',
          true
        ),
      searchType: 'property',
    },
    {
      headerText: 'Search background reports',
      bodyText: 'Find arrest records, contact information and more',
      buttonText: 'Search by name now',
      imgUrl: constants.images.imgSpecializedBackgroundCheck,
      searchType: 'person',

      onCtaClick: () =>
        handleCtaClick('background report', 'openPersonSearch', 'people', true),
    },
    //TODO: Uncomment when these features are ready
    // {
    //   headerText: 'Monitor 10 different reports',
    //   bodyText: 'Get alerted about new data on reports as we become aware',
    //   buttonText: 'See more',
    //   imgUrl: constants.imgBell,

    //   onCtaClick: () => navigate('/dashboard/alertme'),
    // },
    // {
    //   headerText: 'Monitor your online identity',
    //   bodyText:
    //     'Monitor your online identity to get notified if any changes are detected',
    //   buttonText: 'Start monitoring now',
    //   imgUrl: constants.imgIdHub,
    //   onCtaClick: () => navigate('/dashboard/identity-hub'),
    // },
    {
      headerText: 'Dark Web Scan',
      bodyText:
        'See if the email you signed up with may have been leaked in a data breach',
      buttonText: 'Scan now',
      imgUrl: constants.images.imgDarkWebScan,
      onCtaClick: () =>
        handleCtaClick('dark web', '/dashboard/dark-web', 'dark_web', false),
      dataCy: 'dark_web_cta',
      searchType: 'dark_web',
    },
    {
      headerText: 'Unclaimed Money',
      bodyText: 'Did you know 1 in 10 Americans have unclaimed Money?',
      buttonText: 'Find out now',
      imgUrl: constants.images.imgSpecializedUnclaimedMoney,
      onCtaClick: () =>
        handleCtaClick(
          'unclaimed money',
          '/dashboard/unclaimed-money',
          'unclaimed_money',
          false
        ),
      dataCy: 'unclaimed_cta',
      searchType: 'unclaimed_money',
    },
  ];

  const filteredProps = props.filter(
    (prop) => !isHidden(prop.searchType || '')
  );

  return {
    ctaCardProps: filteredProps,
    length: filteredProps.length,
  };
};
// END HELPER_FUNCTIONS
